/**
 * InfluenceDescription component — "How your words shaped this image"
 *
 * Displays below the generated image in the refinement flow.
 * Shows a warm description of how the participant's conversation
 * influenced the AI-generated image.
 *
 * Design: DENiZEN
 * - denizen-card with teal left border accent
 * - Loading skeleton during fetch
 */

export interface InfluenceDescriptionProps {
  /** The influence description text */
  description: string | null
  /** Whether the description is still loading */
  isLoading: boolean
  /** Error from fetching the description (informational, non-blocking) */
  error?: string | null
}

export function InfluenceDescription({
  description,
  isLoading,
  error,
}: InfluenceDescriptionProps) {
  if (!description && !isLoading && !error) return null

  if (error && !description && !isLoading) {
    return (
      <p className="text-xs font-body text-muted italic">
        Could not load description
      </p>
    )
  }

  return (
    <div className="denizen-card border-l-4 border-l-teal p-5 bg-white">
      <h4 className="text-sm font-display font-black text-dark-green mb-3 flex items-center gap-2">
        <span className="w-5 h-5 bg-teal border border-dark-green/15 rounded-full flex items-center justify-center flex-shrink-0">
          <svg
            className="w-3 h-3 text-white"
            fill="currentColor"
            viewBox="0 0 20 20"
            aria-hidden="true"
          >
            <path
              fillRule="evenodd"
              d="M18 13V5a2 2 0 00-2-2H4a2 2 0 00-2 2v8a2 2 0 002 2h3l3 3 3-3h3a2 2 0 002-2zM5 7a1 1 0 011-1h8a1 1 0 110 2H6a1 1 0 01-1-1zm1 3a1 1 0 100 2h3a1 1 0 100-2H6z"
              clipRule="evenodd"
            />
          </svg>
        </span>
        How Your Words Shaped This
      </h4>

      {isLoading ? (
        <div className="space-y-2 animate-pulse" aria-label="Loading influence description">
          <div className="h-4 bg-stone border border-dark-green/10 rounded-2xl w-full" />
          <div className="h-4 bg-stone border border-dark-green/10 rounded-2xl w-5/6" />
          <div className="h-4 bg-stone border border-dark-green/10 rounded-2xl w-4/6" />
        </div>
      ) : (
        <p className="text-sm font-body text-dark-green leading-relaxed break-words">
          {description}
        </p>
      )}
    </div>
  )
}

export default InfluenceDescription
